import { useEffect, useState } from 'react'
import { Icon } from '@iconify/react'
import { useCampaignTotpStepUp } from '@/contexts/CampaignTotpStepUpContext'
import {
  DONOR_PRIZE_STATUS_OPTIONS,
  updateDistributedPrizeStatus,
  type DonorPrize,
  type DonorPrizeStatus,
} from '@/lib/roulette'
import styles from './CampanhasPage.module.css'

export interface RoletaDistributedPrizeStatusCellProps {
  campaignId: number
  prize: DonorPrize
  token: string | null
  disabled?: boolean
  readOnly?: boolean
  onUpdated: (updated: DonorPrize) => void
}

export function RoletaDistributedPrizeStatusCell({
  campaignId,
  prize,
  token,
  disabled = false,
  readOnly = false,
  onUpdated,
}: RoletaDistributedPrizeStatusCellProps) {
  const { runWithStepUp } = useCampaignTotpStepUp()
  const [status, setStatus] = useState<DonorPrizeStatus>(prize.status)
  const [isSaving, setIsSaving] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  useEffect(() => {
    if (!isSaving) {
      setStatus(prize.status)
    }
  }, [prize.id, prize.status, isSaving])

  const handleChange = async (nextStatus: DonorPrizeStatus) => {
    if (readOnly || nextStatus === status || isSaving || disabled) {
      return
    }

    const previousStatus = status
    setStatus(nextStatus)
    setIsSaving(true)
    setErrorMessage(null)

    try {
      const updated = await runWithStepUp(() =>
        updateDistributedPrizeStatus(
          campaignId,
          prize.id,
          { status: nextStatus },
          token ?? undefined
        )
      )
      setStatus(updated.status)
      onUpdated(updated)
    } catch {
      setStatus(previousStatus)
      setErrorMessage('Não foi possível salvar o status.')
    } finally {
      setIsSaving(false)
    }
  }

  const statusLabel =
    DONOR_PRIZE_STATUS_OPTIONS.find((option) => option.value === status)?.label ?? status

  if (readOnly || prize.prize_type === 'coupon' || prize.prize_type === 'lucky_number') {
    return (
      <span className={styles.roletaStatusReadOnly}>
        {prize.prize_type === 'lucky_number' && status === 'used'
          ? 'Entregue'
          : statusLabel}
      </span>
    )
  }

  return (
    <div className={styles.roletaStatusCell}>
      <div className={styles.roletaStatusSelectWrap}>
        <select
          className={styles.roletaStatusSelect}
          value={status}
          disabled={disabled || isSaving}
          aria-label={`Status do prêmio de ${prize.donor?.name ?? 'comprador'}`}
          onChange={(event) => void handleChange(event.target.value as DonorPrizeStatus)}
        >
          {DONOR_PRIZE_STATUS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {isSaving ? (
          <span className={styles.roletaStatusSaving} aria-live="polite">
            <span className={styles.roletaStatusSpinner} aria-hidden />
            Salvando...
          </span>
        ) : null}
      </div>
      {errorMessage ? (
        <p className={styles.roletaStatusError} role="alert">
          <Icon icon="mdi:alert-circle-outline" aria-hidden />
          {errorMessage}
        </p>
      ) : null}
    </div>
  )
}
